import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { DownloadIcon, ExternalLinkIcon } from 'lucide-react'
import { priceSentence, RENEWAL_REMINDER_DAYS } from '@hedgehog/shared'
import { api, ApiRequestError, formatPrice } from '@/lib/api'
import { formatDate } from '@/components/app/format'
import { TabGate, useOrder } from '@/components/app/order'
import { FormError, errorMessage } from '@/components/app/states'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'

export const Route = createFileRoute('/o/$orderId/billing')({
  component: () => (
    <TabGate tab="/o/$orderId/billing">
      <Billing />
    </TabGate>
  ),
})

function Billing() {
  const order = useOrder()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function openPortal() {
    setBusy(true)
    setError(null)
    try {
      const res = await api<{ url: string }>(`/orders/${encodeURIComponent(order.id)}/portal`, { method: 'POST' })
      window.location.assign(res.url)
      return
    } catch (err) {
      if (err instanceof ApiRequestError && err.code === 'payments_disabled') {
        setError('Online billing is not set up for your account yet. We send your invoices by email; reply to any of them with questions.')
      } else if (err instanceof ApiRequestError && (err.status === 401 || err.status === 403)) {
        setError('Your session has ended. Open the link from your latest email, then try again.')
      } else {
        setError(errorMessage(err))
      }
    }
    setBusy(false)
  }

  return (
    <div className="flex flex-col gap-8">
      <section aria-labelledby="plan" className="flex flex-col gap-3">
        <h2 id="plan" className="text-xl font-semibold">
          Your plan
        </h2>
        <dl className="divide-y divide-border rounded-xl border border-border">
          <div className="flex items-center justify-between gap-4 px-4 py-3">
            <dt>Site rebuild and first year of hosting, security and editing, once</dt>
            <dd className="font-medium tabular-nums">{formatPrice(order.priceCents, order.currency)}</dd>
          </div>
          <div className="flex items-center justify-between gap-4 px-4 py-3">
            <dt>Hosting, security and editing from the second year</dt>
            <dd className="font-medium tabular-nums">
              {order.renewalYearlyCents == null ? 'Not set yet' : `${formatPrice(order.renewalYearlyCents, order.currency)} / year`}
            </dd>
          </div>
          {order.renewsAt && (
            <div className="flex items-center justify-between gap-4 px-4 py-3">
              <dt>First year ends</dt>
              <dd className="font-medium tabular-nums">{formatDate(order.renewsAt)}</dd>
            </div>
          )}
        </dl>
        <p className="text-muted-foreground">
          {priceSentence(order)} We email you {RENEWAL_REMINDER_DAYS} days before each renewal.
        </p>
        {order.state === 'refunded' && <p className="text-muted-foreground">This order was refunded.</p>}
      </section>

      <section aria-labelledby="manage" className="flex flex-col items-start gap-3">
        <h2 id="manage" className="text-xl font-semibold">
          Invoices and payment details
        </h2>
        {order.paymentsEnabled ? (
          <>
            <p className="text-muted-foreground">
              Download invoices, change your card or billing address, and cancel the yearly renewal in our payment
              provider's secure billing page (Stripe).
            </p>
            <Button size="lg" onClick={openPortal} disabled={busy}>
              {busy ? <Spinner aria-hidden /> : <ExternalLinkIcon aria-hidden />}
              Open billing page
            </Button>
            <FormError message={error} />
          </>
        ) : (
          <p className="text-muted-foreground">
            We send your invoices by email. To change billing details or cancel, reply to any of our emails.
          </p>
        )}
      </section>

      <section aria-labelledby="cancel" className="flex flex-col gap-3">
        <h2 id="cancel" className="text-xl font-semibold">
          If you cancel
        </h2>
        <ul className="flex list-disc flex-col gap-2 pl-5 text-muted-foreground">
          <li>Your site stays online until the end of the year you paid for, and for at least 30 days after you cancel.</li>
          <li>We email you an export of your content and a static copy of your site that you can host anywhere.</li>
          <li>Your domain and email stay yours. Nothing is locked to us.</li>
        </ul>
        <p>
          <a
            href={`/api/v1/orders/${encodeURIComponent(order.id)}/export`}
            download
            className="inline-flex items-center gap-1.5 underline underline-offset-4"
          >
            <DownloadIcon className="size-4" aria-hidden />
            Download your content and a static copy of your site
          </a>
          <span className="text-sm text-muted-foreground"> (a zip file, available at any time)</span>
        </p>
      </section>
    </div>
  )
}
